import { Loader2 } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

/* ─── Бейдж ─────────────────────────────────────────────────────────────── */

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-muted border-border',
  accent: 'bg-accent-soft text-accent border-transparent',
  success: 'bg-success-soft text-success border-transparent',
  warning: 'bg-warning-soft text-warning border-transparent',
  danger: 'bg-danger-soft text-danger border-transparent',
  info: 'bg-info-soft text-info border-transparent',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
  dot,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[12px] font-medium whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/* ─── Загрузка ──────────────────────────────────────────────────────────── */

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-5 w-5 animate-spin text-muted', className)} aria-hidden />;
}

export function LoadingScreen({ label }: { label?: string }) {
  return (
    <div className="flex min-h-[240px] w-full flex-col items-center justify-center gap-3">
      <Spinner className="h-6 w-6" />
      {label && <p className="text-sm text-muted">{label}</p>}
    </div>
  );
}

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('animate-pulse rounded-lg bg-surface-2', className)}
      aria-hidden
      {...props}
    />
  );
}

/* ─── Пустое состояние ──────────────────────────────────────────────────── */

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2 text-subtle [&>svg]:h-5 [&>svg]:w-5">
          {icon}
        </div>
      )}
      <h3 className="text-[15px] font-semibold text-text">{title}</h3>
      {description && <p className="mt-1.5 max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ─── Разделитель и метки ───────────────────────────────────────────────── */

export function Divider({ className }: { className?: string }) {
  return <div className={cn('h-px w-full bg-border', className)} />;
}

export function Label({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('text-[11px] font-medium uppercase tracking-wide text-subtle', className)}>
      {children}
    </span>
  );
}

/* ─── Сумма денег ───────────────────────────────────────────────────────── */

export type MoneyTone = 'auto' | 'in' | 'out' | 'planned' | 'plain';

export function Money({
  children,
  tone = 'plain',
  size = 'md',
  className,
}: {
  children: ReactNode;
  tone?: MoneyTone;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const tones: Record<MoneyTone, string> = {
    auto: '',
    in: 'text-money-in',
    out: 'text-money-out',
    planned: 'text-money-planned',
    plain: 'text-text',
  };
  const sizes = {
    sm: 'text-[13px]',
    md: 'text-sm',
    lg: 'text-lg font-semibold',
    xl: 'text-3xl font-semibold tracking-tight',
  };

  return <span className={cn('tabular', tones[tone], sizes[size], className)}>{children}</span>;
}
